import { useEffect, useRef, useState } from 'react';
import { clampNumberField, parseNumberField } from '../lib/number-field.js';

interface NumberInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  /** Whole numbers only: truncates decimals, steps by 1, numeric keyboard. */
  integer?: boolean;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  'aria-label'?: string;
}

/**
 * Numeric input that behaves the way users expect: you can clear the field and
 * type a fresh value (the raw text is held locally, so it is never snapped back
 * to 0 mid-edit), the +/- stepper moves by whole units in `integer` mode, and
 * the mobile keyboard omits the decimal point for integers. Replaces the bare
 * `<input type="number" value={someNumber}>` pattern, which forced you to
 * select the digits before overwriting and stepped by fractional amounts
 * (WMS-19).
 */
export function NumberInput({
  value,
  onChange,
  integer = false,
  min,
  max,
  step,
  className,
  ...rest
}: NumberInputProps) {
  const resolvedStep = step ?? (integer ? 1 : undefined);
  const toText = (v: number | null) => (v == null || Number.isNaN(v) ? '' : String(v));
  const [text, setText] = useState(() => toText(value));
  const editing = useRef(false);

  // Sync from the model when it changes externally (reset, stepper elsewhere),
  // but never overwrite what the user is actively typing.
  useEffect(() => {
    if (!editing.current) setText(toText(value));
  }, [value]);

  function emit(raw: string) {
    setText(raw);
    const parsed = parseNumberField(raw, integer);
    if (parsed === undefined) return; // keep the prior model value on garbage input
    onChange(parsed);
  }

  function normalizeOnBlur() {
    editing.current = false;
    let v = value;
    if (v != null && Number.isFinite(v)) {
      v = clampNumberField(v, { integer, min, max });
      if (v !== value) onChange(v);
    }
    setText(toText(v));
  }

  return (
    <input
      {...rest}
      type="number"
      inputMode={integer ? 'numeric' : 'decimal'}
      step={resolvedStep}
      min={min}
      max={max}
      value={text}
      className={className}
      onFocus={() => {
        editing.current = true;
      }}
      onChange={(e) => emit(e.target.value)}
      onBlur={normalizeOnBlur}
    />
  );
}
